import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SparklesIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  UserGroupIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon,
  ClockIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Intersection Observer for fade-in animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all sections with the animate-on-scroll class
    document.querySelectorAll(".animate-on-scroll").forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleLoginClick = () => {
    setIsTransitioning(true);

    // Check if user is already logged in
    const token = localStorage.getItem("auth_token");
    const destination = token ? "/dashboard" : "/login";

    setTimeout(() => {
      navigate(destination);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-dark-bg overflow-hidden">
      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-dark-bg/60 backdrop-blur-xl" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="mx-8 flex justify-between items-center h-16">
            <div
              className={`flex items-center gap-2 ${
                isTransitioning
                  ? "transform-gpu translate-x-[40vw] translate-y-[40vh] scale-150 transition-all duration-700 ease-in-out"
                  : ""
              }`}
            >
              <img
                src="/notuilogo-white.png"
                alt="Notui"
                className={`h-8 w-auto ${
                  isTransitioning
                    ? "scale-[3] transition-all duration-700 ease-in-out"
                    : ""
                }`}
              />
              <span
                className={`text-xl font-bold text-white ${
                  isTransitioning
                    ? "opacity-0 transition-all duration-300 ease-in-out"
                    : ""
                }`}
              >
                Notui
              </span>
            </div>
            <div
              className={`flex items-center gap-6 ${
                isTransitioning
                  ? "opacity-0 transition-all duration-300 ease-in-out"
                  : ""
              }`}
            >
              <button
                onClick={() => navigate("/pricing")}
                className="px-4 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Pricing
              </button>
              <button
                onClick={handleLoginClick}
                className="px-4 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Add a transition overlay */}
      <div
        className={`fixed inset-0 z-40 bg-dark-bg transition-opacity duration-700 ${
          isTransitioning ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <main className="relative bg-dark-bg">
        {/* Hero Section - Full viewport */}
        <section className="min-h-screen flex items-center relative">
          <div className="absolute inset-0 bg-gradient-to-b from-dark-bg via-transparent to-dark-bg pointer-events-none" />

          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-blue-500/20 rounded-full blur-[120px]" />
            <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-purple-500/20 rounded-full blur-[120px]" />
          </div>

          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full animate-on-scroll opacity-0">
            <div className="text-center">
              <div className="inline-block mb-4 px-4 py-1 bg-gray-800/50 rounded-full text-gray-300 text-sm border border-gray-800/50 backdrop-blur-sm">
                ✨ From Notes to Top Grades
              </div>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-gray-200 to-gray-400">
                Transform Your Notes
                <br />
                Into Success
              </h1>
              <p className="text-lg sm:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Stop stressing over exams. Turn your class notes into organized,
                <br className="hidden sm:block" />
                AI-powered study materials and ace your tests.
              </p>
              <button
                onClick={() => navigate("/pricing")}
                className="px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-all duration-200 text-lg font-medium"
              >
                Get Started for Free
              </button>
            </div>
          </div>
        </section>

        {/* Features Section - Full viewport */}
        <section
          id="features"
          className="min-h-screen flex items-center relative bg-dark-bg"
        >
          {/* Add subtle pattern overlay */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="h-full w-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2V6h4V4H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />
          </div>

          {/* Add floating shapes */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-20 right-10 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full relative">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-20">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  Simplify. Study. Succeed.
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Everything you need to turn your notes into your ultimate
                  study tool.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                {[
                  {
                    icon: <SparklesIcon className="w-6 h-6 text-blue-500" />,
                    title: "Smart Summaries",
                    description:
                      "Get instant summaries of your documents, presentations, and study materials",
                    gradient: "from-blue-500/10 to-transparent",
                  },
                  {
                    icon: (
                      <DocumentTextIcon className="w-6 h-6 text-purple-500" />
                    ),
                    title: "Intelligent Notes",
                    description:
                      "Create and organize your notes with AI-powered assistance",
                    gradient: "from-purple-500/10 to-transparent",
                  },
                  {
                    icon: (
                      <AcademicCapIcon className="w-6 h-6 text-green-500" />
                    ),
                    title: "Custom Tests",
                    description:
                      "Generate personalized tests to reinforce your learning",
                    gradient: "from-green-500/10 to-transparent",
                  },
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="group bg-dark-card rounded-xl p-6 hover:bg-dark-hover/50 transition-all duration-500 relative overflow-hidden"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-b ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    />
                    <div className="relative z-10">
                      <div className="p-3 bg-gray-800/50 rounded-lg w-fit mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Full viewport */}
        <section className="min-h-screen flex items-center relative bg-dark-bg">
          {/* Add diagonal stripes */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="h-full w-full"
              style={{
                backgroundImage: `linear-gradient(45deg, transparent 45%, #ffffff 45%, #ffffff 55%, transparent 55%)`,
                backgroundSize: "20px 20px",
              }}
            />
          </div>

          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full relative">
            <div className="animate-on-scroll opacity-0">
              <div className="bg-dark-card rounded-xl p-8 sm:p-12 text-center relative overflow-hidden group">
                {/* Gradient border using outline instead of border */}
                <div className="absolute inset-0">
                  {/* Default outline */}
                  <div className="absolute inset-0 outline outline-1 outline-gray-800 rounded-xl transition-opacity duration-500 group-hover:opacity-0" />

                  {/* Gradient outline */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 rounded-xl outline outline-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500" />
                    <div className="absolute inset-[1px] bg-dark-card rounded-xl" />
                  </div>
                </div>

                {/* Subtle floating elements */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-500/[0.02] rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-purple-500/[0.02] rounded-full blur-3xl" />

                <div className="relative z-10">
                  <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                    Ready to Transform Your Study Process?
                  </h2>
                  <p className="text-gray-400 mb-8 max-w-2xl mx-auto text-lg">
                    Be one of the first to turn your notes into your secret
                    weapon for exam success.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-all duration-200 text-lg font-medium"
                  >
                    Get Started for Free
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="min-h-screen flex items-center relative border-none">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  Why Choose Notui?
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Transform your study experience with powerful AI-driven
                  features
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    title: "Personalized, Straight from Your Notes",
                    description:
                      "Notui creates personalized tests from your notes, helping you focus on key topics and study efficiently for every exam.",
                    icon: <SparklesIcon className="w-6 h-6 text-blue-500" />,
                  },
                  {
                    title: "Exam-Ready Study Materials",
                    description:
                      "Automatically generate summaries, ask the AI to answer questions about YOUR notes, and practice tests from your notes to make exam preparation effortless.",
                    icon: (
                      <DocumentTextIcon className="w-6 h-6 text-purple-500" />
                    ),
                  },
                  {
                    title: "Customizable for Every User",
                    description:
                      "Whether you're a student, professional, or team, Notui adapts to your needs with features like custom AI Q&A and pricing plans to suit any budget.",
                    icon: <UserGroupIcon className="w-6 h-6 text-green-500" />,
                  },
                  {
                    title: "Affordable & Transparent Pricing",
                    description:
                      "Starting at just $5/month for students, Notui delivers advanced AI tools and powerful features to enhance learning without breaking the bank.",
                    icon: <ClockIcon className="w-6 h-6 text-orange-500" />,
                  },
                ].map((benefit, index) => (
                  <div
                    key={index}
                    className="flex gap-4 p-6 bg-dark-card rounded-xl hover:bg-dark-hover/50 transition-all duration-500"
                  >
                    <div className="p-3 bg-gray-800/50 rounded-lg h-fit">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-400">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="min-h-screen flex items-center relative">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  Simple, Transparent Pricing
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Choose the plan that fits your needs
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    name: "Free",
                    price: "$0",
                    period: "forever",
                    features: [
                      "50 AI chat requests/month",
                      "15 summaries/month",
                      "10 detailed AI notes/month",
                      "5 test generations/month",
                      "Basic note organization",
                      "Community support",
                    ],
                  },
                  {
                    name: "Student",
                    price: "$5",
                    period: "per month",
                    features: [
                      "1,200 AI chat requests",
                      "200 document summaries",
                      "120 detailed AI notes",
                      "90 test generations",
                      "Advanced note organization",
                      "Priority support",
                    ],
                    popular: true,
                  },
                  {
                    name: "Professional",
                    price: "$25",
                    period: "per month",
                    features: [
                      "Unlimited AI chat requests",
                      "Unlimited document summaries",
                      "360 detailed AI notes",
                      "270 test generations",
                      "Advanced note organization",
                      "Priority support",
                    ],
                  },
                ].map((plan, index) => (
                  <div
                    key={index}
                    className={`p-8 rounded-xl relative overflow-hidden group ${
                      plan.popular
                        ? "bg-gradient-to-b from-blue-500/20 to-purple-500/20 border border-blue-500/20"
                        : "bg-dark-card"
                    }`}
                  >
                    {plan.popular && (
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 bg-blue-500/20 rounded-full text-blue-400 text-sm">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {plan.name}
                    </h3>
                    <div className="mb-6">
                      <span className="text-3xl font-bold text-white">
                        {plan.price}
                      </span>
                      <span className="text-gray-400 ml-1">/{plan.period}</span>
                    </div>
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-gray-400"
                        >
                          <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate("/pricing")}
                      className={`w-full py-2 rounded-lg transition-colors ${
                        plan.popular
                          ? "bg-white text-gray-900 hover:bg-gray-100"
                          : "bg-gray-800 text-white hover:bg-gray-700"
                      }`}
                    >
                      {plan.name === "Free" ? "Get Started" : "Subscribe Now"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="min-h-screen flex items-center relative">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  How Notui Works
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Get started in minutes
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                <div className="space-y-8">
                  {[
                    {
                      step: "01",
                      title: "Upload Your Content",
                      description: "Import your study materials in any format",
                    },
                    {
                      step: "02",
                      title: "AI Processing",
                      description: "Our AI analyzes and organizes your content",
                    },
                    {
                      step: "03",
                      title: "Study Smarter",
                      description:
                        "Access your enhanced study materials anywhere",
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="text-sm text-blue-500 font-mono">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-dark-card rounded-xl aspect-square"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="min-h-screen flex items-center relative">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  What Students Say
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Join thousands of satisfied students
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    quote:
                      "Notui has completely transformed how I study. The AI summaries are incredible!",
                    author: "Sarah K.",
                    role: "Medical Student",
                    gradient: "from-blue-500/10 to-transparent",
                  },
                  {
                    quote:
                      "The personalized test generation from my own notes is a game-changer. It's like having a personal tutor who knows exactly what I'm studying.",
                    author: "Arshaq M.",
                    role: "Engineering Student, IIUM",
                    gradient: "from-purple-500/10 to-transparent",
                  },
                  {
                    quote:
                      "I used to spend hours organizing my lecture notes. Now Notui does it automatically and even helps me understand difficult concepts through AI Q&A.",
                    author: "Merwan A.",
                    role: "Computer Science Student, IIUM",
                    gradient: "from-green-500/10 to-transparent",
                  },
                  {
                    quote:
                      "Being able to generate practice questions from my own notes has significantly improved my exam preparation. The AI really understands the material.",
                    author: "Huzaifa R.",
                    role: "Business Student, IIUM",
                    gradient: "from-blue-500/10 to-transparent",
                  },
                  {
                    quote:
                      "The smart summaries feature helps me quickly review key concepts before exams. It's like having a study guide that knows exactly what I need.",
                    author: "Faisal K.",
                    role: "Law Student, IIUM",
                    gradient: "from-purple-500/10 to-transparent",
                  },
                  {
                    quote:
                      "As an international student, the AI's ability to explain complex topics in simple terms has been invaluable. Best study tool I've ever used!",
                    author: "Saif H.",
                    role: "Architecture Student, IIUM",
                    gradient: "from-green-500/10 to-transparent",
                  },
                ].map((testimonial, index) => (
                  <div
                    key={index}
                    className="group p-6 bg-dark-card rounded-xl relative overflow-hidden transition-all duration-500 hover:bg-dark-hover/50"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-b ${testimonial.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    />
                    <div className="relative z-10">
                      <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-blue-500 mb-4" />
                      <p className="text-gray-300 mb-4 group-hover:text-white transition-colors duration-500">
                        {testimonial.quote}
                      </p>
                      <div>
                        <p className="text-white font-semibold">
                          {testimonial.author}
                        </p>
                        <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-500">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section className="min-h-screen flex items-center relative">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full">
            <div className="animate-on-scroll opacity-0">
              <div className="text-center mb-16">
                <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4">
                  Join Our Community
                </h2>
                <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                  Connect with fellow students and share your success stories
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-8 bg-dark-card rounded-xl group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    {/* Instagram Icon - You'll need to import this */}
                    <svg
                      className="w-12 h-12 text-purple-500 mb-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Follow us on Instagram
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Get study tips, success stories, and stay updated with the
                      latest features
                    </p>
                    <a
                      href="https://instagram.com/notui"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Join @notui
                      <ArrowRightIcon className="w-5 h-5" />
                    </a>
                  </div>
                </div>

                <div className="p-8 bg-dark-card rounded-xl group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    {/* Discord Icon - You'll need to import this */}
                    <svg
                      className="w-12 h-12 text-blue-500 mb-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Join our Discord
                    </h3>
                    <p className="text-gray-400 mb-6">
                      Connect with other students, share study resources, and
                      get help when you need it
                    </p>
                    <a
                      href="https://discord.gg/notui"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Join the server
                      <ArrowRightIcon className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="min-h-screen flex items-center relative bg-dark-bg">
          {/* Add diagonal stripes */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="h-full w-full"
              style={{
                backgroundImage: `linear-gradient(45deg, transparent 45%, #ffffff 45%, #ffffff 55%, transparent 55%)`,
                backgroundSize: "20px 20px",
              }}
            />
          </div>

          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 w-full relative">
            <div className="animate-on-scroll opacity-0">
              <div className="bg-dark-card rounded-xl p-8 sm:p-12 text-center relative overflow-hidden group">
                {/* Gradient border using outline instead of border */}
                <div className="absolute inset-0">
                  {/* Default outline */}
                  <div className="absolute inset-0 outline outline-1 outline-gray-800 rounded-xl transition-opacity duration-500 group-hover:opacity-0" />

                  {/* Gradient outline */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 rounded-xl outline outline-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500" />
                    <div className="absolute inset-[1px] bg-dark-card rounded-xl" />
                  </div>
                </div>

                {/* Subtle floating elements */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-500/[0.02] rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-purple-500/[0.02] rounded-full blur-3xl" />

                <div className="relative z-10">
                  <h2 className="text-4xl sm:text-6xl font-bold text-white mb-6">
                    Stop Stressing Over Exams
                  </h2>
                  <p className="text-gray-300 text-xl mb-8 max-w-2xl mx-auto">
                    Turn your notes into your ultimate study tool and get
                    exam-ready with Notui
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-all duration-200 text-lg font-medium"
                  >
                    Get Started for Free
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-12">
          <div className="mt-12 pt-8 border-t border-gray-800 flex justify-between items-center">
            <span className="text-gray-400">
              © 2024 Notui. All rights reserved.
            </span>
            <div className="flex gap-6">
              <a
                href="/privacy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy
              </a>
              <a
                href="/terms"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
